import type { Action, BattleActor, BattleAttack, BattleOutcome, BattleRound, Zone } from '@mezha/api'

/** Zones of a blow; the empty zone (no blow) has no name. */
export type BlowZone = Exclude<Zone, ''>
export const ZONE_RU: Record<BlowZone, string> = { head: 'голова', body: 'корпус', legs: 'ноги' }
export const ZONE_TITLE: Record<BlowZone, string> = { head: 'Голова', body: 'Корпус', legs: 'Ноги' }
/** Where a blow goes: «в голову», «в корпус», «в ноги». */
export const ZONE_INTO: Record<BlowZone, string> = { head: 'в голову', body: 'в корпус', legs: 'в ноги' }
export const ACTION_RU: Record<Action, string> = { strike: 'удар', onslaught: 'натиск', charm: 'памятный знак', rest: 'передышка', skill: 'умение' }

/** Prices and gains of the common actions of mezha-2 (server internal/combat/rules.go). */
export const ONSLAUGHT_FOCUS = 2
export const CHARM_MANA = 4
export const REST_FOCUS = 2
export const REST_MANA = 3
export const TWILIGHT_PCT = 25

/** Statuses of the engine (add-schools-skills design §5): the name and one phrase about it. */
export const STATUS_RU: Record<string, string> = {
  bleed: 'кровотечение', stun: 'оглушение', silence: 'забытьё', sunder: 'надлом', named: 'назван', trip: 'подсечка', pinned: 'пригвождён',
  seal: 'печать', disarm: 'обезоружен', hobbled: 'по ногам', ward: 'оберег', circle: 'круг-оберег', song: 'песня пути', last_stand: 'узел',
  circle_rule: 'правило круга',
}
export const STATUS_TEXT: Record<string, string> = {
  bleed: 'теряет здоровье в конце раунда',
  stun: 'не бьёт и не применяет приёмов; защита действует',
  silence: 'только удар или передышка',
  sunder: 'броня ниже',
  named: 'не уклоняется, получает больше урона',
  trip: 'закрытая зона пропускает половину удара',
  pinned: 'защищает ту же зону, что в прошлом раунде',
  seal: 'следующий прошедший удар слабее',
  disarm: 'бьёт без оружия и не парирует',
  hobbled: 'не уклоняется, бьёт слабее',
  ward: 'поглотит первую попытку статуса и часть урона',
  circle: 'входящий урон меньше',
  song: 'точнее бьёт, +1 напор за раунд',
  last_stand: 'смертельный удар оставит 1 здоровья',
  circle_rule: 'удар врага в закрытую зону отнимает у него напор',
}

const OUTCOME_RU: Record<string, string> = { miss: 'промах', dodge: 'уклонение', parry: 'парирование' }
/** Why a fighter left, in the grammatical gender of the name (male, female, neuter). */
const OUT_RU: Record<string, [string, string, string]> = {
  down: ['повержен', 'повержена', 'повержено'],
  forfeit: ['сдался', 'сдалась', 'сдалось'],
  withdrawn: ['выбыл: два пропущенных хода', 'выбыла: два пропущенных хода', 'выбыло: два пропущенных хода'],
  '': ['выбыл', 'выбыла', 'выбыло'],
}
const GENDER_INDEX: Record<string, number> = { male: 0, female: 1, neuter: 2 }

/** A word about a fighter in the gender of its name. */
export function outText(reason: string | null | undefined, gender: string): string {
  return (OUT_RU[reason ?? ''] ?? OUT_RU[''])[GENDER_INDEX[gender] ?? 0]
}

export type LogPart = { text: string; tone?: 'hit' | 'blk' }
export type LogLine = LogPart[]

/** One attack in words: who hit whom where, what the defence did, the damage. */
export function attackLine(a: BattleAttack, name: (id: string) => string, auto: (id: string) => boolean, skill: (id: string) => string = id => id): LogLine {
  const who = name(a.attacker)
  const whom = name(a.target)
  if (a.outcome === 'splash') {
    const parts: LogLine = [{ text: `${who}${auto(a.attacker) ? ' (автоход)' : ''} — ${skill(a.skill) || 'размах'} задевает ${whom}: ` },
      { text: a.applied > 0 ? `−${a.applied}` : 'без урона', tone: 'hit' }]
    if (a.absorbed) parts.push({ text: `, оберег принял ${a.absorbed}`, tone: 'blk' })
    for (const st of a.statuses) parts.push(statusPart(st.id, st.result))
    return parts
  }
  const how = a.skill ? `«${skill(a.skill)}»${a.hit > 1 ? `, удар ${a.hit},` : ''}` : a.action === 'onslaught' ? 'с натиском' : a.action === 'charm' ? 'под памятным знаком' : a.kind === 'spirit' ? 'знаком' : ''
  const head: LogPart = { text: `${who}${auto(a.attacker) ? ' (автоход)' : ''} — ${a.group_secondary ? 'по соседней цели: ' : ''}${how ? `${how} ` : ''}${a.attack ? ZONE_INTO[a.attack] : 'без удара'} (${whom}): ` }
  if (a.outcome in OUTCOME_RU) return [head, { text: OUTCOME_RU[a.outcome], tone: 'blk' }]
  const parts: LogLine = [head]
  if (a.outcome === 'crit') parts.push({ text: 'крит ', tone: 'hit' })
  parts.push({ text: `−${a.applied}`, tone: 'hit' })
  if (a.first_blow) parts.push({ text: ', первый удар копьём' })
  if (a.guarded) parts.push({ text: `, в ${a.second ? 'вторую ' : ''}защиту: прошло ${a.guard_pass || 25}%` })
  if (a.blocked) parts.push({ text: ', щит принял половину', tone: 'blk' })
  if (a.absorbed) parts.push({ text: `, оберег принял ${a.absorbed}`, tone: 'blk' })
  if (a.group_pressure) parts.push({ text: ', усилен натиском группы' })
  for (const st of a.statuses) parts.push(statusPart(st.id, st.result))
  return parts
}

/** A status attempt in words: set, resisted, taken by a ward, no effect. */
function statusPart(id: string, result: string): LogPart {
  const name = STATUS_RU[id] ?? id
  switch (result) {
    case 'applied': return { text: `, ${name}` }
    case 'absorbed': return { text: `, оберег отвёл: ${name}`, tone: 'blk' }
    case 'immune': return { text: `, ${name} не действует`, tone: 'blk' }
    default: return { text: `, ${name} не прошло`, tone: 'blk' }
  }
}

/** A resolved round: twilight, breathers, attacks, guesses of the guard,
 *  bleeding and who left the fight. */
export function roundLines(r: BattleRound, actors: BattleActor[], skillNames: Record<string, string> = {}): LogLine[] {
  const name = (id: string) => actors.find(a => a.id === id)?.name ?? id
  const skill = (id: string) => skillNames[id] ?? id
  const gender = (id: string) => actors.find(a => a.id === id)?.gender ?? 'male'
  const auto = (id: string) => Boolean(r.actors.find(a => a.id === id)?.choice.auto)
  const lines: LogLine[] = []
  if (r.twilight) lines.push([{ text: `Сумерки: удары сильнее на ${TWILIGHT_PCT}%`, tone: 'hit' }])
  for (const a of r.actors) {
    if (a.choice.action === 'rest' && !a.choice.auto) lines.push([{ text: `${name(a.id)}: передышка, ` }, { text: `+${REST_FOCUS} напора${r.twilight ? '' : `, +${REST_MANA} маны`}`, tone: 'blk' }])
  }
  for (const e of r.effects) {
    const by = e.skill ? `«${skill(e.skill)}»` : ''
    const on = e.target === e.actor ? '' : ` (${name(e.target)})`
    switch (e.kind) {
      case 'status': lines.push([{ text: `${name(e.actor)} — ${by}${on}` }, statusPart(e.status, e.result)]); break
      case 'heal': lines.push([{ text: `${name(e.actor)} — ${by}${on}: ` }, { text: e.value > 0 ? `+${e.value} здоровья` : 'исцеление не дошло', tone: 'blk' }]); break
      case 'cleanse': lines.push([{ text: `${name(e.actor)} — ${by}${on}: ` }, { text: e.value > 0 ? `сняты дурные статусы (${e.value})` : 'снимать нечего', tone: 'blk' }]); break
      case 'ward': lines.push([{ text: `${name(e.actor)} — ${by}${on}: ` }, { text: 'оберег на раунд', tone: 'blk' }]); break
      case 'last_stand': lines.push([{ text: `${name(e.target)}: узел удержал на ногах — ` }, { text: '1 здоровья', tone: 'blk' }]); break
    }
  }
  lines.push(...r.attacks.map(a => attackLine(a, name, auto, skill)))
  for (const a of r.actors) {
    if (a.guard_gain) lines.push([{ text: `${name(a.id)}: удачная защита, ` }, { text: '+1 напор', tone: 'blk' }])
  }
  for (const a of r.actors) {
    if (a.bleed_tick) lines.push([{ text: `${name(a.id)}: кровотечение ` }, { text: `−${a.bleed_tick}`, tone: 'hit' }])
    if (a.out) lines.push([{ text: `${name(a.id)} ${outText(a.out_reason, gender(a.id))}` }])
  }
  return lines
}

/** Why the battle ended, from the player's side (reasons of the combat rules). */
export function reasonText(o: BattleOutcome, won: boolean, draw: boolean): string {
  switch (o.reason) {
    case 'knockout': return draw ? 'пали обе стороны' : won ? 'противник повержен' : 'вы повержены'
    case 'rounds': return 'бой закончился по числу раундов — решило оставшееся здоровье'
    case 'abandoned': return 'два пропущенных хода подряд'
    case 'withdrawn': return won ? 'противник вышел из боя' : 'вы вышли из боя'
    case 'admin_abort': return 'бой остановлен администрацией'
    default: return o.reason
  }
}
